// Privacy mode's one question, answered in one place (task 402.10): "should this field be shortened on screen,
// and how?". The vocabulary comes from the api (api/console/config/sensitive-fields), so a property newly marked
// [Sensitive] there is covered here without a second edit.

import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../../api/client';
import { usePrivacyStore } from '../../stores/privacyStore';
import type { SensitiveFieldCatalog } from '../../stores/privacyStore';
import { maskValue, MASK_FORMATS } from './maskValue';

export type { SensitiveField, SensitiveFieldCatalog } from '../../stores/privacyStore';

export const privacyKeys = {
  catalog: () => ['privacy', 'sensitive-fields'] as const,
};

export function getSensitiveFieldCatalog(): Promise<SensitiveFieldCatalog> {
  return api.get<SensitiveFieldCatalog>('api/console/config/sensitive-fields');
}

/**
 * Loads the vocabulary once, in the shell. Deliberately NOT inside `usePrivacy`: that hook is called by every
 * cell on a page, and making a display helper require a QueryClient would force one on every test and every
 * caller. Failing is fine — masking falls back to its own list, which covers more rather than less.
 */
export function usePrivacyCatalogLoader(): void {
  const setCatalog = usePrivacyStore((s) => s.setCatalog);
  const { data } = useQuery({
    queryKey: privacyKeys.catalog(),
    queryFn: getSensitiveFieldCatalog,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (data) setCatalog(data);
  }, [data, setCatalog]);
}

/**
 * What privacy mode covers when the api cannot be asked. Deliberately generous: if the catalog is unavailable we
 * would rather shorten something ordinary than leave a customer's name on a shared screen. Kept in step with the
 * api by `privacy.test.ts`, which fails if the api's own list grows a name shape this misses.
 */
const FALLBACK: Record<string, string> = {
  fullname: MASK_FORMATS.firstNameLastInitial,
  displayname: MASK_FORMATS.firstNameLastInitial,
  label: MASK_FORMATS.firstNameLastInitial,
  counterpartyname: MASK_FORMATS.firstNameLastInitial,
  localaccountname: MASK_FORMATS.firstNameLastInitial,
  firstname: MASK_FORMATS.firstNameLastInitial,
  lastname: MASK_FORMATS.initial,
  email: MASK_FORMATS.email,
  phone: MASK_FORMATS.last4,
  mobile: MASK_FORMATS.last4,
  taxid: MASK_FORMATS.last4,
  dateofbirth: MASK_FORMATS.yearOnly,
  iban: MASK_FORMATS.first4last4,
  accountnumber: MASK_FORMATS.first4last4,
  counterpartyaccountnumber: MASK_FORMATS.first4last4,
  addressline1: MASK_FORMATS.hidden,
  addressline2: MASK_FORMATS.hidden,
  street: MASK_FORMATS.hidden,
  city: MASK_FORMATS.hidden,
  postalcode: MASK_FORMATS.hidden,
};

const COUNTRY = /(country|nationality|residence)/i;

/** Resolves a field name to its mask format, or null when the field is not personal data. */
export function maskFormatFor(
  fieldName: string,
  catalog: SensitiveFieldCatalog | undefined,
): string | null {
  if (COUNTRY.test(fieldName)) return null; // countries are always visible (owner Q1)

  if (catalog) {
    const hit = catalog.fields.find((f) => f.name.toLowerCase() === fieldName.toLowerCase());
    if (hit) return hit.mask;
    // The api answered and does not class this field: trust it over the fallback.
    return null;
  }

  return FALLBACK[fieldName.toLowerCase()] ?? null;
}

export interface Privacy {
  /** Privacy mode is on. */
  on: boolean;
  /** Masks a value when privacy mode is on and the field is personal data; otherwise returns it unchanged. */
  mask: (fieldName: string, value: unknown) => unknown;
  /** True when this field would be shortened right now — for a lock icon beside it. */
  covers: (fieldName: string) => boolean;
}

export function usePrivacy(): Privacy {
  const on = usePrivacyStore((s) => s.enabled);
  const catalog = usePrivacyStore((s) => s.catalog);

  return {
    on,
    covers: (fieldName: string) => on && maskFormatFor(fieldName, catalog) !== null,
    mask: (fieldName: string, value: unknown) => {
      if (!on) return value;
      const format = maskFormatFor(fieldName, catalog);
      return format === null ? value : maskValue(value, format);
    },
  };
}
